import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, MapPin, Phone, Mail, Clock, AlertCircle } from 'lucide-react';
import ConsumerNav, { ConsumerFooter } from '@/components/ConsumerNav';
import { useRegion } from '@/contexts/RegionContext';
import { toast } from 'sonner';
import axios from 'axios';
import { useBusinessHours, formatBusinessHoursSummary } from '@/hooks/useBusinessHours';
import { cleanPhone } from '@/lib/inputUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  'Cabinet Refacing',
  'Full Kitchen Remodel',
  'Countertops',
  'Backsplash',
  'Hardware & Fixtures',
  'Just Exploring',
];

const BUDGET_OPTIONS = [
  'Under $5,000',
  '$5,000 – $10,000',
  '$10,000 – $20,000',
  '$20,000 – $35,000',
  '$35,000+',
  'Not sure yet',
];

const TIMELINE_OPTIONS = [
  'ASAP',
  '1 – 3 months',
  '3 – 6 months',
  '6 – 12 months',
  'Not sure yet',
];

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  postalCode: '',
  serviceInterest: '',
  budget: '',
  timeline: '',
  notes: '',
};

export const ContactSection = ({ compact = false }) => {
  const { postalLabel, postalPlaceholder } = useRegion();
  const { hours } = useBusinessHours();
  const hoursSummary = formatBusinessHoursSummary(hours);
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10;
  };

  const validateField = (field, value) => {
    let error = '';
    
    if (field === 'name' && !value.trim()) {
      error = 'Name is required';
    } else if (field === 'email') {
      if (!value.trim()) {
        error = 'Email is required';
      } else if (!validateEmail(value)) {
        error = 'Please enter a valid email address';
      }
    } else if (field === 'phone') {
      if (!value.trim()) {
        error = 'Phone number is required';
      } else if (!validatePhone(value)) {
        error = 'Please enter a valid phone number (10+ digits)';
      }
    }
    
    return error;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    
    // Real-time validation for touched fields
    if (touched[field]) {
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, form[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    const newErrors = {};
    ['name', 'email', 'phone'].forEach((field) => {
      const error = validateField(field, form[field]);
      if (error) newErrors[field] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({ name: true, email: true, phone: true });
      toast.error('Please fix the errors before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        source: 'contact_form',
        status: 'new',
      });
      setSubmitted(true);
      toast.success('Message sent! We\'ll be in touch shortly.');
      setErrors({});
      setTouched({});
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-6" data-testid="contact-success">
        <div className="w-16 h-16 rounded-full bg-brass/10 flex items-center justify-center mb-6">
          <CheckCircle className="h-8 w-8 text-brass" />
        </div>
        <h3 className="font-display text-2xl mb-3">Thank you!</h3>
        <p className="text-muted-foreground max-w-md">
          We've received your message and will be in touch within one business day.
        </p>
        {!compact && (
          <Button className="btn-accent mt-8" onClick={() => setSubmitted(false)}>
            Send Another Message
          </Button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="contact-name">Full Name <span className="text-destructive">*</span></Label>
          <Input
            id="contact-name"
            data-testid="contact-name"
            placeholder="Jane Smith"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            className={errors.name && touched.name ? 'border-destructive' : ''}
            required
          />
          {errors.name && touched.name && (
            <div className="flex items-center gap-1 mt-1 text-xs text-destructive">
              <AlertCircle className="h-3 w-3" />
              <span>{errors.name}</span>
            </div>
          )}
        </div>
        <div>
          <Label htmlFor="contact-email">Email <span className="text-destructive">*</span></Label>
          <Input
            id="contact-email"
            type="email"
            data-testid="contact-email"
            placeholder="jane@example.com"
            value={form.email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            className={errors.email && touched.email ? 'border-destructive' : ''}
            required
          />
          {errors.email && touched.email && (
            <div className="flex items-center gap-1 mt-1 text-xs text-destructive">
              <AlertCircle className="h-3 w-3" />
              <span>{errors.email}</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="contact-phone">Phone <span className="text-destructive">*</span></Label>
          <Input
            id="contact-phone"
            type="tel"
            inputMode="tel"
            data-testid="contact-phone"
            placeholder="(416) 555-0100"
            value={form.phone}
            onChange={(e) => handleChange('phone', cleanPhone(e.target.value))}
            onBlur={() => handleBlur('phone')}
            className={errors.phone && touched.phone ? 'border-destructive' : ''}
            required
          />
          {errors.phone && touched.phone && (
            <div className="flex items-center gap-1 mt-1 text-xs text-destructive">
              <AlertCircle className="h-3 w-3" />
              <span>{errors.phone}</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1">Format: (xxx) xxx-xxxx or xxx-xxx-xxxx</p>
        </div>
        <div>
          <Label htmlFor="contact-postal">{postalLabel}</Label>
          <Input
            id="contact-postal"
            data-testid="contact-postal"
            placeholder={postalPlaceholder}
            value={form.postalCode}
            onChange={(e) => handleChange('postalCode', e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label>Service Interest</Label>
        <Select value={form.serviceInterest} onValueChange={(v) => handleChange('serviceInterest', v)}>
          <SelectTrigger data-testid="contact-service">
            <SelectValue placeholder="What are you looking for?" />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label>Budget Range</Label>
          <Select value={form.budget} onValueChange={(v) => handleChange('budget', v)}>
            <SelectTrigger data-testid="contact-budget">
              <SelectValue placeholder="Select a range" />
            </SelectTrigger>
            <SelectContent>
              {BUDGET_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Timeline</Label>
          <Select value={form.timeline} onValueChange={(v) => handleChange('timeline', v)}>
            <SelectTrigger data-testid="contact-timeline">
              <SelectValue placeholder="When are you planning?" />
            </SelectTrigger>
            <SelectContent>
              {TIMELINE_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="contact-message">Message</Label>
        <Textarea
          id="contact-message"
          data-testid="contact-message"
          placeholder="Tell us a bit about your project..."
          rows={4}
          value={form.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
        />
      </div>

      <Button
        type="submit"
        className="btn-accent w-full py-6 text-base"
        disabled={submitting}
        data-testid="contact-submit"
      >
        {submitting ? 'Sending...' : 'Send Message'}
      </Button>
    </form>
  );
};

const ContactPage = () => {
  const { hours } = useBusinessHours();
  const hoursSummary = formatBusinessHoursSummary(hours);
  return (
    <div className="min-h-screen bg-white">
      <ConsumerNav activeLink="Contact" />

      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 px-6 lg:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block text-brass text-xs uppercase tracking-[0.2em] font-medium mb-4">
            Get In Touch
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-kx-charcoal mb-6">
            Let's Talk About<br />
            <span className="text-brass">Your Kitchen</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ready to reimagine your space? Fill out the form below and one of our design experts will be in touch within one business day.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-16">
          {/* Info column */}
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="font-display text-2xl mb-6">Contact Information</h2>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-brass/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Phone className="h-4 w-4 text-brass" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Phone</p>
                    <p className="text-muted-foreground text-sm mt-0.5">1-800-KXPRESS</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-brass/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Mail className="h-4 w-4 text-brass" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Email</p>
                    <p className="text-muted-foreground text-sm mt-0.5">info@kitchnxpress.ca</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-brass/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Clock className="h-4 w-4 text-brass" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Hours</p>
                    <p className="text-muted-foreground text-sm mt-0.5" data-testid="contact-business-hours">{hoursSummary}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-brass/10 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="h-4 w-4 text-brass" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Service Area</p>
                    <p className="text-muted-foreground text-sm mt-0.5">Greater Toronto Area & surrounding regions</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-cream rounded-xl p-6">
              <h3 className="font-display text-lg mb-3">Prefer to start online?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Try our free kitchen visualizer and see your transformation before committing.
              </p>
              <a href="/#path-selection">
                <Button className="btn-accent w-full" data-testid="contact-visualizer-cta">
                  Start Free Design
                </Button>
              </a>
            </div>
          </div>

          {/* Form column */}
          <div className="lg:col-span-3">
            <h2 className="font-display text-2xl mb-8">Send Us a Message</h2>
            <ContactSection />
          </div>
        </div>
      </section>

      <ConsumerFooter />
    </div>
  );
};

export default ContactPage;
